import { isEmpty } from 'lodash'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card as CardIcon, Country, Email, HelpCircle, MapPin, Mobile, Teleph } from '@yy/one-icon'
import { OnePopover } from '@yy/one-ui'
import type { OrderAddressInfoVo } from '@/services/apis/order/type'
import { useSelector } from '@/store'
import type { IAddress } from '@/store/addressSlice/types'
import { concatAddress, filterText, formatStoreAddress } from '@/utils'
import styles from './index.module.less'
import { consigneeCertificateTypeOption } from '@/bizComponent/ServiceProvider/components/Form/ExtraFormItem/components'

export const CERTIFICATE_TYPES_MAP = consigneeCertificateTypeOption.reduce((map, op) => {
  map[op.value] = op.label
  return map
}, {})

export const formatSenderAddress = (address: IAddress): AddressItemType => ({
  name: address?.senderName,
  mobile: address?.senderMobile || address?.senderPhone,
  phone: address?.senderPhone,
  email: address?.senderEmail,
  country: address?.senderCountryCode,
  zipCode: address?.senderZipCode,
  addressTwo: address?.senderAddressTwo,
  addressOne: address?.senderAddress,
  town: address?.senderTown,
  district: address?.senderDistrict,
  city: address?.senderCity,
  province: address?.senderProvince,
})
export const formatConsigneeAddress = (address: IAddress): AddressItemType => ({
  name: address?.consigneeName,
  mobile: address?.consigneeMobile || address?.consigneePhone,
  email: address?.consigneeEmail,
  country: address?.consigneeCountryCode,
  zipCode: address?.consigneeZipCode,
  addressTwo: address?.consigneeAddressTwo,
  addressOne: address?.consigneeAddress,
  town: address?.consigneeTown,
  district: address?.consigneeDistrict,
  city: address?.consigneeCity,
  province: address?.consigneeProvince,
  idNo: address?.consigneeIdNo,
  idType: address?.consigneeIdType,
  stationAddress: address?.consigneeStationAddress || address?.consigneeAddress,
  stationName: address?.consigneeStationName,
  stationCode: address?.consigneeStationCode,
})

export function formatConsigneeAddressVo(address: OrderAddressInfoVo): AddressItemType {
  return {
    name: address?.name,
    mobile: address?.phone,
    country: address?.countryCode,
    addressTwo: address?.addressTwo,
    addressOne: address?.address,
    town: address?.town,
    district: address?.district,
    city: address?.city,
    province: address?.province,
  }
}

export interface AddressItemType {
  name?: string
  mobile?: string
  phone?: string
  email?: string
  country?: string
  /** 寄件邮编 */
  zipCode?: string
  /** 证件类型 */
  idType?: number
  /** 证件号 */
  idNo?: string
  addressTwo?: string
  addressOne?: string
  town?: string
  district?: string
  city?: string
  province?: string
  stationAddress?: string
  stationName?: string
  stationCode?: string
}
export interface AddressItemProps {
  address: AddressItemType
  taxNo?: string
  taxType?: string
  nameLabel?: string
  hasPrivacy?: boolean
  /** 是否展示手机区号 */
  showAreaCode?: boolean
}
const AddressItem: React.FC<AddressItemProps> = (props) => {
  const { address, taxNo, taxType, nameLabel, hasPrivacy, showAreaCode } = props
  const { t } = useTranslation()
  const countryMapDics = useSelector((state) => state.dictionary.countryMapDics)
  /** 手机区号映射 */
  const areaCodeMap = useSelector((state) => state.common?.areaCodeMap)

  const addressDetail = useMemo(() => {
    return concatAddress({
      addressTwo: address?.addressTwo,
      address: address?.stationCode ? formatStoreAddress(address?.stationAddress, address?.stationName, address.stationCode) : address?.addressOne,
      town: address?.town,
      district: address?.district,
      city: address?.city,
      province: address.province,
    })
  }, [
    address?.addressOne,
    address?.addressTwo,
    address?.city,
    address?.district,
    address.province,
    address?.stationAddress,
    address.stationCode,
    address?.stationName,
    address?.town,
  ])
  return (
    <div className={styles.addressItem}>
      <div className="flex align_c">
        {/* 姓名 */}
        {address?.name ? (
          <h4 style={{ color: '#00112F', fontWeight: '400' }}>
            {nameLabel ? (
              <span>
                {`${nameLabel}: `}
                <span style={{ fontWeight: '500' }}>{address?.name}</span>
              </span>
            ) : (
              ''
            )}
          </h4>
        ) : null}
        {/* 个人隐私申诉 */}
        {hasPrivacy ? (
          <OnePopover content={t('user.appeal.tip')}>
            <HelpCircle className={styles.helpIcon} />
          </OnePopover>
        ) : null}
      </div>
      <div className={styles.row}>
        {/* 手机 */}
        {address?.mobile ? (
          <span className={styles.item}>
            <Mobile className={styles.icon} />
            {/* 区号 */}
            {showAreaCode && address?.country && <span>{areaCodeMap[address.country] ? areaCodeMap[address?.country] : ''}</span>}
            <span className={styles.text}>{address?.mobile}</span>
          </span>
        ) : null}
        {/* 电话 */}
        {address?.phone ? (
          <span className={styles.item}>
            <Teleph className={styles.icon} />
            <span className={styles.text}>{address?.phone}</span>
          </span>
        ) : null}
        {/* 邮箱 */}
        {address?.email ? (
          <span className={styles.item}>
            <Email className={styles.icon} />
            <span className={styles.text}>{address?.email}</span>
          </span>
        ) : null}
        {/* 国家 */}
        {address?.country ? (
          <span className={styles.item}>
            <Country className={styles.icon} /> {filterText(countryMapDics?.[address?.country]?.countryName)}
          </span>
        ) : null}
      </div>
      <div>
        {/* 详细地址 */}
        {!isEmpty(addressDetail) || countryMapDics?.[address?.country]?.countryName || address?.zipCode ? (
          <span className={styles.row}>
            <MapPin className={styles.icon} />
            <span className={styles.wordWrap}>
              {addressDetail || ''},{filterText(countryMapDics?.[address?.country]?.countryName)} {address?.zipCode || ''}
            </span>
          </span>
        ) : null}
        {/* 证件类型-号码 */}
        {address?.idType || address?.idNo ? (
          <div className={styles.row}>
            <CardIcon className={styles.icon} />
            <span>
              {t(CERTIFICATE_TYPES_MAP[address?.idType])}-{address?.idNo}
            </span>
          </div>
        ) : null}
        {/* 税号类型-号码 */}
        {taxNo || taxType ? (
          <div className={styles.row}>
            <CardIcon className={styles.icon} />
            <span>
              {taxType}-{taxNo}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  )
}
AddressItem.displayName = 'AddressItem'
export default AddressItem
